import React from "react";
import { Avatar, Typography, Box } from "@mui/material";
import {
  HandHeart,
  UserCirclePlus,
  CalendarDots,
  ReadCvLogo,
  Files,
  FileX,
  Butterfly,
  WarningCircle,
} from "@phosphor-icons/react";
import getAvatarInitials from "utils/getAvatarInitials";
import { theme } from "@answersnow/pkg-theme";

const formatText = (text) => {
  // Split by * and map through parts
  return text.split(/\*(.+?)\*/).map((part, index) => {
    // Every odd index is between ** and should be styled with bold
    if (index % 2 === 1) {
      return (
        <Box key={index} component="span" fontWeight={500}>
          {part}
        </Box>
      );
    }
    return (
      <Box key={index} component="span" color="text.secondary">
        {part}
      </Box>
    );
  });
};

export const getNotificationIcon = (notification) => {
  const metadata = notification.metadata || {};
  if (notification.type === "clinical") {
    switch (metadata.action) {
      case "client_assigned":
        return <UserCirclePlus size={20} weight="duotone" />;
      case "schedule_assessment":
      case "schedule_treatment_plan_review":
      case "assessment_scheduled":
        return <CalendarDots size={20} weight="duotone" />;
      case "auth_submitted":
      case "auth_approved":
        return <HandHeart size={20} weight="duotone" />;
      case "upload_treatment_plan":
      case "onboarding_packet_completed":
      case "diagnostic_document_uploaded":
        return <ReadCvLogo size={20} weight="duotone" />;
      case "tech_check_tablet_recommended":
        return <Butterfly size={20} weight="duotone" color={theme.palette.green[600]} />;
      case "document_uploaded":
        return <Files size={20} weight="duotone" />;
      case "treatment_plan_suggestion_ready":
        return <ReadCvLogo size={20} weight="duotone" />;
      case "confirm_deactivation":
        return <WarningCircle size={20} weight="duotone" />;
      case "document_awaiting_supervisor_signature":
      case "document_awaiting_director_signature":
        return (
          <Avatar sx={{ width: 32, height: 32, fontSize: 16 }}>
            {getAvatarInitials(metadata.client_name)}
          </Avatar>
        );
      case "ai_session_note_completed":
        return notification.content === "failed" ? (
          <FileX size={20} weight="duotone" color={theme.palette.error.main} />
        ) : (
          <Butterfly size={20} weight="duotone" color={theme.palette.green[600]} />
        );
    }
  }

  return (
    <Avatar src={metadata.image_url} sx={{ width: 32, height: 32, fontSize: 16 }}>
      {getAvatarInitials(metadata.sender_name)}
    </Avatar>
  );
};

export const getMessageNotificationBody = (notification) => {
  const metadata = notification.metadata || {};
  return (
    <Typography variant="body2">
      <Box component="span" fontWeight={500}>
        {metadata.sender_name || "Unknown"}
      </Box>{" "}
      <Box component="span" color="text.secondary">
        sent a message to
      </Box>{" "}
      <Box component="span" fontWeight={500}>
        AnswersNow
      </Box>
    </Typography>
  );
};

export const getNotificationBody = (notification) => {
  switch (notification.type) {
    case "message":
      return getMessageNotificationBody(notification);
    case "clinical":
      return getClinicalNotificationBody(notification);
    case "matching":
      return getMatchingNotificationBody(notification);
    default:
      return <Typography variant="body2">Unknown notification type</Typography>;
  }
};

export const getClinicalNotificationBody = (notification) => {
  const metadata = notification.metadata || {};

  switch (metadata.action) {
    case "client_assigned":
      return (
        <Typography variant="body2">
          {formatText(`*${metadata.client_name || "client"}* was added to your caseload`)}
        </Typography>
      );
    case "schedule_assessment":
      return (
        <Typography variant="body2">
          {formatText(
            `It's time to schedule *${metadata.client_name || "client"}'s assessment appointment*`
          )}
        </Typography>
      );
    // ANOW-6970. The toast carries a "View Profile" button, so here the call to action has to
    // be spelled out instead — the whole row is what the clinician clicks.
    case "assessment_scheduled":
      return (
        <Typography variant="body2">
          {formatText(
            `New Assessment Call Scheduled with *${metadata.client_name || "client"}*. Click to view profile.`
          )}
        </Typography>
      );
    case "schedule_treatment_plan_review":
      return (
        <Typography variant="body2">
          {formatText(
            `It's time to schedule *${metadata.client_name || "client"}'s* *Treatment Plan Review appointment* `
          )}
        </Typography>
      );
    case "sdoh_assessment_completed":
      return (
        <Typography variant="body2">
          {formatText(
            `*${metadata.client_name || "Client"}'s Social Determinants of Health* survey is ready for your review.`
          )}
        </Typography>
      );
    case "auth_submitted":
      return (
        <Typography variant="body2">
          {formatText(
            `An *Insurance Authorization* has been submitted for *${metadata.client_name || "client"}*`
          )}
        </Typography>
      );
    case "auth_approved":
      return (
        <Typography variant="body2">
          {formatText(
            `An *Insurance Authorization* has been approved for *${metadata.client_name || "client"}*`
          )}
        </Typography>
      );
    case "upload_treatment_plan":
      return (
        <Typography variant="body2">
          {formatText(
            `Upload and submit *${metadata.client_name || "client"}'s Treatment Plan* to move forward with care.`
          )}
        </Typography>
      );
    case "document_uploaded": {
      const clientName = metadata.client_name || "your client";

      return (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            New client document uploaded
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A new document has been uploaded for{" "}
            <Box component="span" fontWeight={600}>
              {clientName}
            </Box>
            .
          </Typography>
        </Box>
      );
    }
    case "onboarding_packet_completed":
      return (
        <Typography variant="body2">
          {formatText(
            `*${metadata.caregiver_name || "caregiver"}* completed the Onboarding packet for *${metadata.client_name || "client"}.* Click to see the results.`
          )}
        </Typography>
      );
    case "treatment_plan_suggestion_ready":
      return (
        <Typography variant="body2">
          {formatText(
            `*${metadata.client_name || "Your client"}'s* treatment plan suggestion is ready for review.`
          )}
        </Typography>
      );
    case "confirm_deactivation": {
      const clientName = metadata.client_name || "client";

      return (
        <Box>
          <Typography variant="body2" fontWeight={600} style={{ marginBottom: "4px" }}>
            Client deactivation pending your review
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click to review and approve the scheduled deactivation for{" "}
            <Box component="span" fontWeight={600}>
              {clientName}
            </Box>
            .
          </Typography>
        </Box>
      );
    }
    case "ai_session_note_completed":
      return (
        <Typography variant="body2">
          {formatText(
            `*${metadata.client_name || "Your client"}'s session note* ${notification.content === "failed" ? "failed to generate." : "is ready for review."}`
          )}
        </Typography>
      );
    case "diagnostic_document_uploaded": {
      const clientName = metadata.client_name || "a client";

      return (
        <Box>
          <Typography variant="body2" fontWeight={600} style={{ marginBottom: "4px" }}>
            New diagnostic document uploaded
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A new diagnostic document has been uploaded for{" "}
            <Box component="span" fontWeight={600}>
              {clientName}
            </Box>{" "}
            and is ready for review.
          </Typography>
        </Box>
      );
    }
    case "document_awaiting_supervisor_signature": {
      const clientName = metadata.client_name || "Client";
      const documentName = metadata.document_name || "Document";
      return (
        <Typography variant="body2">
          {formatText(`*${clientName} - ${documentName}* is ready to review`)}
        </Typography>
      );
    }
    case "tech_check_tablet_recommended":
      return (
        <Typography variant="body2">
          {formatText(
            `*${metadata.client_name || "Client"}'s* tech check recommends a tablet. The tablet process has started.`
          )}
        </Typography>
      );
    case "document_awaiting_director_signature": {
      const clientName = metadata.client_name || "Client";
      const documentName = metadata.document_name || "Document";
      return (
        <Typography variant="body2">
          {formatText(`*${clientName} - ${documentName}* requires a director signature`)}
        </Typography>
      );
    }
    default:
      return <Typography variant="body2">Unknown notification</Typography>;
  }
};

export const getMatchingNotificationBody = (notification) => {
  const notificationText =
    notification.content?.toUpperCase() === "SUCCESS"
      ? "Matching results have been refreshed."
      : "There was an issue refreshing matching results. The previous results are still available.";
  return <Typography variant="body2">{formatText(notificationText)}</Typography>;
};
